import { DiscountType, Promotion } from "~/entities/promotionEntities";
import { ResponseOrderWithCartLines } from "~/entities/orderEntities";
import { MenuTabItem } from "~/entities/menuEntities";

/** One thing to ask the customer for before the offer can pay out. */
export type RewardStep = {
  label: string;
  qty: number;
  /** Any one of these satisfies the step */
  productCodes: string[];
};

/** How many of these product codes the cart already holds, counting quantity. */
const heldQty = (
  order: ResponseOrderWithCartLines,
  productCodes: string[]
): number =>
  (order.lines ?? []).reduce((total, line) => {
    const code = line.item?.productCode;
    return code && productCodes.includes(code)
      ? total + (line.item?.qty ?? 1)
      : total;
  }, 0);

/**
 * What the customer still needs for this promotion to pay out, as steps to ask
 * them for.
 *
 * DISPLAY ONLY. This decides what to ASK FOR; it never decides whether a
 * discount applied — the server owns that, and orderHelper.ts deliberately
 * refuses to mirror BUNDLE_PRICE application for exactly that reason. If a
 * chosen combination does not form, the checkout guard catches it.
 */
export const resolveOutstandingRequirement = (
  promotion: Promotion,
  order: ResponseOrderWithCartLines
): RewardStep[] => {
  if (promotion.discountType === DiscountType.BUNDLE_PRICE) {
    return (promotion.bundleComponents ?? []).reduce<RewardStep[]>(
      (steps, component) => {
        const outstanding =
          component.qty - heldQty(order, component.productCodes);

        if (outstanding > 0) {
          steps.push({
            label: component.label || "Choose your item",
            qty: outstanding,
            productCodes: component.productCodes,
          });
        }

        return steps;
      },
      []
    );
  }

  // Any promotion that targets specific products needs one of them in the cart
  // before it can discount anything — a 100%-off LINE_DISCOUNT is how a "free
  // item" is actually expressed here, and it discounts a line that must exist.
  //
  // Deliberately NOT keyed on discountType. REWARD_PRODUCT is the obvious
  // candidate and it is a trap: the online engine never applies it
  // (filterAndSortScheduledPromotions buckets by type and the apply path
  // consumes only ORDER/LINE/ROOT_ITEM/CHILD_ITEM/BUNDLE_PRICE), so keying on
  // it would work for exactly the promotions that can never pay out.
  if (promotion.productCodes.length) {
    return heldQty(order, promotion.productCodes) > 0
      ? []
      : [
          {
            label: "Your free item",
            qty: 1,
            productCodes: promotion.productCodes,
          },
        ];
  }

  // Order-level percentage or fixed amount: nothing specific to add, the
  // customer just needs to spend more
  return [];
};

/**
 * The menu item for a product code on the menu currently being browsed.
 *
 * Products live on menus, so a reward that exists at the site is not
 * necessarily on the menu in front of the customer — which is why callers must
 * treat "not found" as a reason to add nothing at all rather than to add part
 * of an offer.
 */
export const findMenuItemByProductCode = (
  menu: { tabs?: { items: MenuTabItem[] }[] } | null,
  productCode: string
): MenuTabItem | undefined =>
  menu?.tabs
    ?.flatMap((tab) => tab.items)
    .find((item) => item.productCode === productCode);
